import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import SearchFormContainer from "Components/atoms/containers/search-form-container"
import TextInput from "Components/molecule/inputs/text-input"
import SearchSelect from "Components/molecule/inputs/search-select"
import { SelectOption } from "Components/molecule/inputs/select-input"
import MultiSelect from "Components/molecule/inputs/multi-select"
import { IBillabilityQuery, BILLABLE_OPTIONS } from "models/billability"
import useEnterSearch from "hooks/useEnterSearch"
import { useMemo } from "react"

interface SearchBillabilityFormProps {
    query: IBillabilityQuery
    employeeClassOptions: SelectOption[]
    departmentOptions: SelectOption[]
    branchOptions: SelectOption[]
    groupByOptions: SelectOption[]
    groupBySelection: string
    handleChangeGroupBy: (value: string) => void
    isSearching: boolean
    handleChangeQuery: (key: string, value: any) => void
    handleSearch: () => void
    handleClearSearch: () => void
}

const SearchBillabilityForm = ({
    query,
    employeeClassOptions,
    departmentOptions,
    branchOptions,
    groupByOptions,
    groupBySelection,
    handleChangeGroupBy,
    isSearching,
    handleChangeQuery,
    handleSearch,
    handleClearSearch,
}: SearchBillabilityFormProps) => {
    const { selectProps } = useEnterSearch(handleSearch)

    const selectedClasses = useMemo(
        () => employeeClassOptions.filter(o => query.employeeClassIds?.includes(o.value)),
        [query, employeeClassOptions]
    )

    const selectedBillable = useMemo(
        () => BILLABLE_OPTIONS.filter(o => query.billable?.includes(o.value)),
        [query]
    )

    const selectedDepartments = useMemo(
        () => departmentOptions.filter(o => query.departmentIds?.includes(o.value)),
        [query, departmentOptions]
    )

    const selectedBranches = useMemo(
        () => branchOptions.filter(o => query.branchIds?.includes(o.value)),
        [query, branchOptions]
    )

    return (
        <SearchFormContainer
            inputs={
                <>
                    <TextInput
                        type="date"
                        label="Date From"
                        value={query.fromDate ?? ''}
                        onChange={(e) => handleChangeQuery('fromDate', e.target.value || undefined)}
                    />
                    <TextInput
                        type="date"
                        label="Date To"
                        value={query.toDate ?? ''}
                        onChange={(e) => handleChangeQuery('toDate', e.target.value || undefined)}
                    />
                    {/* Period, then Branch > Department, then the rest */}
                    <MultiSelect
                        label="Branch"
                        options={branchOptions}
                        value={selectedBranches}
                        onChange={(vals: SelectOption[]) =>
                            handleChangeQuery('branchIds', vals.length ? vals.map(v => v.value) : undefined)
                        }
                    />
                    <MultiSelect
                        label="Department"
                        options={departmentOptions}
                        value={selectedDepartments}
                        onChange={(vals: SelectOption[]) =>
                            handleChangeQuery('departmentIds', vals.length ? vals.map(v => v.value) : undefined)
                        }
                    />
                    <MultiSelect
                        label="Employee Class"
                        options={employeeClassOptions}
                        value={selectedClasses}
                        onChange={(vals: SelectOption[]) =>
                            handleChangeQuery('employeeClassIds', vals.length ? vals.map(v => v.value) : undefined)
                        }
                    />
                    <MultiSelect
                        label="Billable"
                        options={BILLABLE_OPTIONS}
                        value={selectedBillable}
                        onChange={(vals: SelectOption[]) =>
                            handleChangeQuery('billable', vals.length ? vals.map(v => v.value) : undefined)
                        }
                    />
                    <SearchSelect
                        label="Group By"
                        options={groupByOptions}
                        value={groupBySelection}
                        onChange={(e) => e?.value && handleChangeGroupBy(e.value)}
                        {...selectProps}
                    />
                </>
            }
            clearSearchButton={
                <>
                    <PrimaryButton style={{ minWidth: '150px' }} disabled={isSearching} onClick={handleSearch}>
                        {isSearching ? 'Searching...' : 'Search'}
                    </PrimaryButton>
                    <SecondaryButton style={{ minWidth: '100px' }} disabled={isSearching} onClick={handleClearSearch}>
                        Clear Search
                    </SecondaryButton>
                </>
            }
        />
    )
}

export default SearchBillabilityForm
